/**
 * Terminal-state banner for a planning run that failed or stopped responding.
 *
 * Without this the only signal a run died was the progress tracker never
 * finishing, which reads as "still working" for as long as the tab stays open.
 */
import React, { useRef, useState } from 'react';

import { chatService } from '../../services/chatService';
import type { SupervisorButton } from '../../types/supervisorButtons';
import { recordUserActionClick } from '../AiOverviewPanel/useActionBarClicks';
import { PlanningActionTasks } from '../PlanningActionTasks';

const RETRY_BUTTON: SupervisorButton = {
  key: 'continue', kind: 'workflow', action: 'resume_design', label: 'Retry',
  payload: { phase: 'auto' },
};

interface PlanningFailureBannerProps {
  projectId: string | null;
  sessionId?: string | null;
  message: string | null;
  onDismiss: () => void;
}

export const PlanningFailureBanner: React.FC<PlanningFailureBannerProps> = ({
  projectId, sessionId, message, onDismiss,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const retryPending = useRef(false);

  if (!message) return null;

  const runRetry = async (id: string) => {
    try {
      // This banner has no timeline entry; the server resolves the current
      // failed run and validates this source-less retry before creating work.
      const planningActionInvocationId = await recordUserActionClick(
        undefined, 'auto', 'continue', RETRY_BUTTON.label, id, sessionId, { required: true },
      );

      if (!planningActionInvocationId) throw new Error('The retry task could not be prepared.');
      // 'auto' lets the backend pick the phase it can safely replay rather than
      // restarting planning from the top and discarding finished pages.
      await chatService.resumeDesign(id, 'auto', sessionId, planningActionInvocationId);
      onDismiss();
    } catch {
      setRetryError('Retry could not be started. Check your connection and try again.');
    } finally {
      retryPending.current = false;
      setIsRetrying(false);
    }
  };

  const handleRetry = () => {
    if (!projectId || retryPending.current) return;
    retryPending.current = true;
    setIsRetrying(true);
    setRetryError(null);
    void runRetry(projectId);
  };

  return (
    <>
      <PlanningActionTasks projectId={projectId} phase="auto" buttons={[RETRY_BUTTON]} current />
      <div className="planning-error" role="alert">
        <span>{retryError ?? message}</span>
        <button
          className="planning-error__retry-btn"
          onClick={handleRetry}
          disabled={isRetrying || !projectId}
          type="button"
        >
          {isRetrying ? 'Retrying…' : 'Retry'}
        </button>
        <button
          className="planning-error__retry-btn"
          onClick={onDismiss}
          type="button"
        >
          Dismiss
        </button>
      </div>
    </>
  );
};
